import * as signalR from "@microsoft/signalr";
import { IUserNotification } from "../../Interface/Shared/Notification";
import {
  addUserNotification,
  markUserNotificationsSeen,
  updateNotificationCount,
} from "../../Redux/Actions/Index";
import store from "../../Redux/Store/AppStore";
import { getToken, getTokenData } from "../../Utils/Auth";
import { PortalTypeEnum } from "../../Utils/Common/Enums/Enums";
import { consumeQueuedReport } from "../../Utils/Common/QueuedReportTracker";
import { pushReportNotice } from "../../Utils/Common/ReportNotifier";
import NotificationService from "../Account/NotificationService";

/** Groups the SignalR function exposes on /api/labs/{labId}/groups */
export type SignalRGroupType = "pending-count" | "notification";

/**
 * Each SignalR function endpoint has its own Azure Function key.
 * GetAllUserNotifications hands them to the client after login.
 */
type SignalRFunctionOperation = "negotiate" | "joinGroup" | "seen";

class SignalRService {
  // Single connection
  private connection: signalR.HubConnection | null = null;
  private connectionId: string | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 2;
  private reconnectDelay: number = 3000;
  private isInitializing: boolean = false;

  /**
   * Get negotiate URL
   * Uses unified negotiate endpoint: /api/labs/{labId}/negotiate
   * Base URL changes based on environment (qa or production)
   */
  private getNegotiateUrl(): string {
    return `${this.getBaseUrl()}/api/labs/${this.getLabId()}/negotiate`;
  }

  /**
   * Base URL of the SignalR function app for the current environment
   */
  private getBaseUrl(): string {
    return this.getEnvironment() === "qa"
      ? "https://truemeditsignalrfunction-qa.azurewebsites.net"
      : "https://truemeditsignalrfunction.azurewebsites.net";
  }

  /**
   * Lab id the connection belongs to
   */
  private getLabId(): number {
    const state: any = store.getState();
    return state?.Reducer?.selectedTenantInfo?.tenantId || 49;
  }

  /**
   * Logged in user id, used for the notification group and the seen endpoint
   */
  private getUserId(): string {
    return getTokenData()?.userId ?? "";
  }

  /**
   * Headers required by the SignalR function app.
   * The `x-functions-key` differs per endpoint: the keys come from
   * GetAllUserNotifications (`data.functionKeys`) and are kept in Redux.
   * Falls back to the env var, and sends no key at all when neither is
   * available, so an anonymously configured function app still works.
   */
  private getFunctionHeaders(
    operation: SignalRFunctionOperation
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const state: any = store.getState();
    const functionKey =
      state?.Reducer?.notificationFunctionKeys?.[operation] ||
      process.env.REACT_APP_SIGNALR_FUNCTION_KEY;

    if (functionKey) {
      headers["x-functions-key"] = functionKey;
    } else {
      console.warn(`No function key available for the ${operation} endpoint`);
    }

    return headers;
  }

  /**
   * Get the environment (qa or production) based on baseUrl
   */
  private getEnvironment(): string {
    const state: any = store.getState();
    const baseUrl = state?.Reducer?.baseUrl || "";

    // Check if baseUrl contains "qa" to determine environment
    if (baseUrl.toLowerCase().includes("qa")) {
      return "qa";
    }
    return "production";
  }

  /**
   * Initialize SignalR connection
   * Flow:
   * 1. Call negotiate endpoint to get connection URL
   * 2. Extract URL from negotiate response
   * 3. Create Hub connection with the negotiated URL
   * 4. Set up event handlers (listening to events with pattern: event-{environment}-{labId})
   * 5. Start the connection
   */
  async initializeConnection(): Promise<void> {
    // Check if PortalType is Admin - SignalR should only work for Admin portal
    const state: any = store.getState();
    const portalType =
      state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.adminType ||
      state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.portalType;

    if (portalType !== PortalTypeEnum.Admin) {
      console.log("SignalR service is only available for Admin portal type");
      return;
    }

    // Prevent duplicate initialization
    if (this.isInitializing) {
      return;
    }

    // Check if connection already exists and is connected
    if (
      this.connection &&
      this.connection.state === signalR.HubConnectionState.Connected
    ) {
      return;
    }

    // If connection exists but is disconnected, clean it up first
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch {
        // Ignore errors when stopping
      }
      this.connection = null;
    }

    this.isInitializing = true;

    try {
      const token = getToken();
      if (!token) {
        console.warn("No token available for SignalR connection");
        return;
      }

      // Step 1: Call negotiate endpoint
      const negotiateUrl = this.getNegotiateUrl();

      const response = await fetch(negotiateUrl, {
        method: "POST",
        headers: this.getFunctionHeaders("negotiate"),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Negotiate endpoint error:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new Error(
          `Negotiate failed: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      // Try to parse as JSON, but handle text responses too
      let negotiateResponse: any;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        negotiateResponse = await response.json();
      } else {
        const textResponse = await response.text();
        console.warn(
          "Negotiate endpoint returned non-JSON response:",
          textResponse
        );
        try {
          negotiateResponse = JSON.parse(textResponse);
        } catch {
          throw new Error(
            `Negotiate endpoint returned invalid response: ${textResponse}`
          );
        }
      }

      // Step 2: Extract connection URL from negotiate response
      // The reference client deserializes this case-insensitively, so accept
      // both the PascalCase and camelCase spellings.
      const connectionUrl = negotiateResponse.Url ?? negotiateResponse.url;
      const accessToken =
        negotiateResponse.AccessToken ?? negotiateResponse.accessToken;

      // Validate that we have the required connection URL
      if (!connectionUrl) {
        console.error(
          "Negotiate response missing connection URL:",
          negotiateResponse
        );
        throw new Error("Negotiate response does not contain a connection URL");
      }

      // If accessToken is not provided, we might need to use the token from the request
      const finalAccessToken = accessToken || token;

      // Step 3: Create Hub connection with the negotiated URL
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(connectionUrl, {
          accessTokenFactory: () => finalAccessToken,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => {
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
              return this.reconnectDelay;
            }
            return null; // Stop reconnecting after max attempts
          },
        })
        .build();

      // Reset reconnect attempts
      this.reconnectAttempts = 0;

      // Step 4: Set up event handlers (listening to events with pattern: event-{environment}-{labId})
      this.setupEventHandlers();

      // Step 5: Start the connection
      await this.connection.start();
      this.connectionId = this.connection.connectionId;

      // Step 6: Join the groups this connection should receive events for
      await this.joinGroups();
    } catch (error) {
      console.error("Error initializing SignalR connection:", error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => this.initializeConnection(), this.reconnectDelay);
      }
    } finally {
      // Remove from initializing flag
      this.isInitializing = false;
    }
  }

  /**
   * Set up SignalR event handlers
   * Listens on events with pattern: event-{environment}-{labId}
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Get environment and labId for event names
    const state: any = store.getState();
    const labId = state?.Reducer?.selectedTenantInfo?.tenantId;
    const environment = this.getEnvironment();

    if (!labId) {
      console.warn("No labId found, cannot set up event handlers");
      return;
    }

    const eventSuffix = `${environment}-${labId}`;

    // Handle reconnection
    this.connection.onreconnecting(() => {
      // Reconnecting...
    });

    this.connection.onreconnected(async (connectionId) => {
      this.reconnectAttempts = 0;
      // Reconnecting produces a new connection id, so the groups have to be joined again
      this.connectionId = connectionId ?? this.connection?.connectionId ?? null;
      await this.joinGroups();
    });

    this.connection.onclose((error) => {
      this.connection = null;
      this.connectionId = null;

      // Only attempt reconnect if there was an error (not a manual disconnect)
      if (error) {
        this.reconnectAttempts++;
        if (
          this.reconnectAttempts < this.maxReconnectAttempts &&
          !this.isInitializing
        ) {
          setTimeout(() => this.initializeConnection(), this.reconnectDelay);
        }
      }
    });

    // Listen for sales rep pending count updates with environment-labId suffix
    this.connection.on(
      `salesRepPendingCountUpdated-${eventSuffix}`,
      (data: number | { count: number }) => {
        const count = typeof data === "number" ? data : data.count;
        store.dispatch(updateNotificationCount({ salesRepRequest: count }));
      }
    );

    // Listen for requisition pending count updates with environment-labId suffix
    this.connection.on(
      `requisitionPendingCountUpdated-${eventSuffix}`,
      (data: number | { count: number }) => {
        const count = typeof data === "number" ? data : data.count;
        store.dispatch(updateNotificationCount({ pendingRequisition: count }));
      }
    );

    // Listen for facility pending count updates with environment-labId suffix
    this.connection.on(
      `facilityPendingCountUpdated-${eventSuffix}`,
      (data: number | { count: number }) => {
        const count = typeof data === "number" ? data : data.count;
        store.dispatch(updateNotificationCount({ newAccountRequest: count }));
      }
    );

    // Listen for new user notifications (no environment-labId suffix, the notification group is per user)
    this.connection.on("userNotificationReceived", (data: any) => {
      const notification = this.parseEventPayload<IUserNotification>(data);
      if (!notification) {
        return;
      }
      store.dispatch(addUserNotification(notification));

      // Reload from the API as well, so the list and the unread count match the
      // server without waiting for the bell to be opened
      NotificationService.loadUserNotifications().catch((error) => {
        console.error(
          "Failed to refresh notifications after a new one arrived:",
          error
        );
      });

      // Only announce a finished report when this tab is waiting for one
      if (consumeQueuedReport()) {
        pushReportNotice({
          status: "ready",
          title: notification.messageTitle?.trim() || "Your report is ready",
          message:
            notification.message?.trim() ||
            "Open the notification bell to download it.",
          // Stays a little longer than the queued card: the user has been
          // waiting minutes and may not be looking at the screen
          autoCloseMs: 8000,
        });
      }
    });

    // Listen for notifications marked as seen (e.g. from another tab or device of the same user)
    this.connection.on("notificationsSeen", (data: any) => {
      const payload = this.parseEventPayload<{
        notificationIds?: number[];
        NotificationIds?: number[];
        seenAt?: string;
        SeenAt?: string;
        isMarkedAllAsRead?: boolean;
        IsMarkedAllAsRead?: boolean;
      }>(data);
      if (!payload) {
        return;
      }

      const isMarkedAllAsRead = Boolean(
        payload.isMarkedAllAsRead ?? payload.IsMarkedAllAsRead
      );
      const notificationIds =
        payload.notificationIds ?? payload.NotificationIds;

      // Mark all as read carries no ids
      if (
        !isMarkedAllAsRead &&
        (!Array.isArray(notificationIds) || !notificationIds.length)
      ) {
        return;
      }

      store.dispatch(
        markUserNotificationsSeen(
          notificationIds ?? [],
          payload.seenAt ?? payload.SeenAt ?? new Date().toISOString(),
          isMarkedAllAsRead
        )
      );
    });
  }

  /**
   * The function app sends payloads either as an object or as a JSON string,
   * so normalise both into an object.
   */
  private parseEventPayload<T>(data: any): T | null {
    if (!data) {
      return null;
    }

    if (typeof data === "string") {
      try {
        return JSON.parse(data) as T;
      } catch (error) {
        console.error("Unable to parse SignalR event payload:", data, error);
        return null;
      }
    }

    return data as T;
  }

  /**
   * Join the groups for the current connection.
   * POST /api/labs/{labId}/groups with the connection id and the group type.
   *
   * pending-count is lab wide and carries no user id; notification is per user
   * and is skipped entirely when no user id is available.
   */
  private async joinGroups(): Promise<void> {
    const userId = this.getUserId();

    await this.joinGroup("pending-count", null);

    if (userId) {
      await this.joinGroup("notification", userId);
    }
  }

  private async joinGroup(
    groupType: SignalRGroupType,
    userId: string | null
  ): Promise<void> {
    if (!this.connectionId) {
      console.warn(
        `No connection id available, cannot join group ${groupType}`
      );
      return;
    }

    try {
      const response = await fetch(
        `${this.getBaseUrl()}/api/labs/${this.getLabId()}/groups`,
        {
          method: "POST",
          headers: {
            ...this.getFunctionHeaders("joinGroup"),
            "x-signalr-connectionid": this.connectionId,
          },
          body: JSON.stringify({
            GroupType: groupType,
            UserId: userId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to join group ${groupType}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
      }
    } catch (error) {
      console.error(`Error joining group ${groupType}:`, error);
    }
  }

  /**
   * Mark notifications as seen.
   * POST /api/labs/{labId}/{userId}/notifications/seen
   * The function app broadcasts a `notificationsSeen` event back to the user's
   * other connections, which keeps every open tab in sync.
   *
   * `isMarkedAllAsRead` marks every notification of the user as seen, including
   * the ones that are not loaded in the client. It is sent with an empty
   * NotificationIds array.
   */
  async markNotificationsSeen(
    notificationIds: number[],
    isMarkedAllAsRead: boolean = false
  ): Promise<boolean> {
    // The endpoint expects distinct positive integers, and none at all when
    // everything is being marked as read
    const ids = isMarkedAllAsRead
      ? []
      : Array.from(
          new Set(
            (Array.isArray(notificationIds) ? notificationIds : []).filter(
              (id) => Number.isInteger(id) && id > 0
            )
          )
        );

    if (!isMarkedAllAsRead && !ids.length) {
      return false;
    }

    const userId = this.getUserId();
    if (!userId) {
      console.warn("No user id available, cannot mark notifications as seen");
      return false;
    }

    const seenAt = new Date().toISOString();

    try {
      const response = await fetch(
        `${this.getBaseUrl()}/api/labs/${this.getLabId()}/${encodeURIComponent(
          userId
        )}/notifications/seen`,
        {
          method: "POST",
          headers: this.getFunctionHeaders("seen"),
          body: JSON.stringify({
            NotificationIds: ids,
            SeenAt: seenAt,
            IsMarkedAllAsRead: isMarkedAllAsRead,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to mark notifications as seen:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        return false;
      }

      // Update locally as well: the seen event is only broadcast to the other connections
      store.dispatch(
        markUserNotificationsSeen(ids, seenAt, isMarkedAllAsRead)
      );
      return true;
    } catch (error) {
      console.error("Error marking notifications as seen:", error);
      return false;
    }
  }

  /**
   * Disconnect SignalR connection
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
      } finally {
        this.connection = null;
        this.connectionId = null;
        this.reconnectAttempts = 0;
      }
    }
  }

  /**
   * Check if connection is active
   */
  isConnected(): boolean {
    return (
      this.connection !== null &&
      this.connection.state === signalR.HubConnectionState.Connected
    );
  }
}

// Export singleton instance
export const signalRService = new SignalRService();
export default signalRService;
